import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import api from '../../api/axios';
import { APP, ROUTES, STORAGE_KEYS } from '../../config';
import { useAuth } from '../../lib/auth';
import Loader, { ErrorBox } from '../../components/Loader.jsx';

/**
 * Auretics SSO landing page — bridges the Auretics-issued JWT to a
 * Taskydo session.
 *
 * Why this page exists
 * --------------------
 * Auretics deep-links its users into Taskydo with
 *
 *     https://psfl.tasky.do/complaints/sso/login/?token=<auretics-jwt>
 *
 * (the URL contract is owned by Auretics and was first documented in
 * ``docs/sso.md``).  In the legacy server-rendered world Django's
 * ``complaints_management_system.views.sso_login_user`` handled that
 * URL: it validated the JWT, materialised the user inside the tenant
 * schema, wrote a Django session cookie and 302-redirected to
 * ``/complaints/create``.
 *
 * After Nginx started serving the React build for ``/complaints/...``
 * (see ``docs/nginx_snippet.md``), the same URL stopped reaching
 * Django.  Nginx now serves ``index.html``, the SPA's React-Router
 * tries to match ``/complaints/sso/login/``, fails, and renders the
 * generic "Page not found" screen — exactly the bug that prompted
 * this fix.
 *
 * Two complementary fixes are needed and we ship both:
 *
 *   1. Backend: a JSON sibling endpoint at
 *      ``GET /api/complaints/sso/login/?token=...`` (lives in
 *      ``complaints_management_system.views.api_sso_login``).
 *      It validates the same Auretics JWT and returns a Taskydo
 *      JWT pair plus the user's ``/me/`` payload.
 *
 *   2. Frontend (this page): a public SPA route mounted at
 *      ``/complaints/sso/login/`` that calls the JSON endpoint, hands
 *      the returned tokens to ``useAuth().setUser`` /
 *      ``localStorage`` (mirroring what ``AuthProvider.login`` does
 *      after a password login) and ``navigate()``s to
 *      ``/complaints/create`` so the SPA's own routing takes over.
 *
 * The page is intentionally mounted OUTSIDE the ``RequireAuth`` shell
 * so the user can land here without being bounced to ``/login`` —
 * the JWT in the URL IS the credential.
 */
export default function SSOLogin() {
  const location = useLocation();
  const navigate = useNavigate();
  const { setUser } = useAuth();

  // We only want to run the exchange once per mount.  React 18's
  // strict-mode double-invokes effects in development; without this
  // guard we'd hit ``/api/complaints/sso/login/`` twice and (because
  // the JWT has a 5-minute window per ``docs/sso.md``) the second
  // call could legitimately succeed but issue a *new* token pair,
  // leaving us in a confusing race.  Cheap to gate and keeps the
  // network log clean.
  const inFlight = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (inFlight.current) return;
    inFlight.current = true;

    const params = new URLSearchParams(location.search);
    const token = params.get('token');
    // Optional ``next=`` override — lets Auretics deep-link into a
    // specific complaint page after login.  We only honour absolute
    // paths so a malicious ``next=https://evil.tld/`` can't open-
    // redirect off-site.
    const rawNext = params.get('next');
    const next = rawNext && rawNext.startsWith('/') ? rawNext : null;

    if (!token) {
      setError(new Error('Missing SSO token in URL.'));
      return;
    }

    (async () => {
      try {
        // Hit the JSON sibling of the legacy session endpoint.  The
        // axios instance from ``src/api/axios.js`` already targets
        // ``window.location.origin + '/api'`` so the tenant subdomain
        // is preserved — exactly what django-tenants needs to pick
        // the right schema.  We do NOT need to send the access token
        // we currently have (if any); the Auretics JWT is the
        // credential here.
        const { data } = await api.get('/complaints/sso/login/', {
          params: { token },
        });

        if (!data || !data.access) {
          throw new Error('SSO response missing access token.');
        }

        // Persist the Taskydo JWT pair + user record exactly the way
        // ``AuthProvider.login`` does after a password login, so the
        // rest of the app — ``RequireAuth``, axios interceptor,
        // ``RequirePermission`` — sees a fully-bootstrapped session.
        localStorage.setItem(STORAGE_KEYS.ACCESS, data.access);
        if (data.refresh) {
          localStorage.setItem(STORAGE_KEYS.REFRESH, data.refresh);
        }
        if (data.user) {
          localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(data.user));
          setUser(data.user);
        }

        // Where to drop the user.  ``next=`` query param wins; then
        // the backend's recommendation; then a sensible default.
        const target =
          next ||
          (typeof data.redirect_to === 'string' && data.redirect_to.startsWith('/')
            ? data.redirect_to
            : '/complaints/create?from_sso=true');

        navigate(target, { replace: true });
      } catch (err) {
        setError(err);
      }
    })();
    // We deliberately depend ONLY on location.search — re-running on a
    // new query string is the right call (different SSO link), but we
    // don't want re-renders triggered by ``setUser`` to re-fire the
    // exchange.  ``inFlight`` belt-and-braces guards strict-mode too.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // ------- render -------
  return (
    <div className="ty-login">
      <div className="ty-login__card" role="status" aria-live="polite">
        <div
          aria-hidden
          style={{
            width: 64,
            height: 64,
            margin: '0 auto 14px',
            borderRadius: 18,
            display: 'grid',
            placeItems: 'center',
            color: '#fff',
            fontSize: 28,
            fontWeight: 800,
            letterSpacing: '-0.02em',
            background: 'linear-gradient(135deg, #7980ff 0%, #656cfd 100%)',
            boxShadow: '0 12px 28px rgba(121, 128, 255, 0.35)',
          }}
        >
          {(APP.name || 'T').slice(0, 1).toUpperCase()}
        </div>
        <h1 className="ty-login__title">{APP.name}</h1>
        <p className="ty-login__sub">Signing you in via Auretics SSO…</p>

        {error ? (
          <>
            <ErrorBox error={error} />
            <p style={{ marginTop: 16, textAlign: 'center' }}>
              <a className="ty-btn" href={ROUTES.LOGIN}>
                Go to login
              </a>
            </p>
          </>
        ) : (
          <Loader label="Verifying token…" />
        )}
      </div>
    </div>
  );
}
